import { useMemo, useState } from "react";
import { useTalkgroupActivity, useTalkgroups, useTick } from "../mcptt/hooks";
import type { Group } from "@mcptt/client";
import type { TalkgroupActivity } from "../mcptt/hooks";

interface Props {
  networkId:      string | null;
  networkName:    string | undefined;
  selectedId:     string | null;
  onSelect:       (id: string) => void;
  /** Talkgroup currently being transmitted by this dispatcher. */
  activePttGroupId: string | null;
  mutedGroupIds:  ReadonlySet<string>;
  pinnedGroupIds: ReadonlySet<string>;
  onPttStart:      (id: string) => void;
  onPttStop:       (id: string) => void;
}

/** Center-top — talkgroup tile grid per design-handoff variant A.
 *  Each tile: name + priority chip + last-talker callsign + since-time
 *  + RX/TX state. Sorted by priority (highest first), then alpha.
 *
 *  Action buttons (TXT/LOC) only render on the selected tile to keep
 *  the grid compact at a glance. The legacy CA/PT/EMG actions don't
 *  exist in the FleetConsole wire and are documented in lib/README. */
export function TalkgroupsPanel({
  networkId, networkName, selectedId, onSelect, activePttGroupId,
  mutedGroupIds, pinnedGroupIds, onPttStart, onPttStop,
}: Props) {
  const groups   = useTalkgroups(networkId);
  const activity = useTalkgroupActivity(networkId);
  const now      = useTick(1000);

  const sorted = useMemo(
    () => [...groups].sort((a, b) => {
      const ap = pinnedGroupIds.has(a.ID) ? 0 : 1;
      const bp = pinnedGroupIds.has(b.ID) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      const pa = a.Priority ?? 0;
      const pb = b.Priority ?? 0;
      if (pa !== pb) return pb - pa;
      return a.Name.localeCompare(b.Name);
    }),
    [groups, pinnedGroupIds],
  );

  const liveCount = useMemo(() => {
    let n = 0;
    for (const g of sorted) {
      const a = activity.get(g.ID);
      if (a && (a.state === "tx" || a.state === "rx" || a.state === "hangtime")) n++;
    }
    return n;
  }, [sorted, activity]);

  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const flash = (msg: string) => {
    setActionMsg(msg);
    window.setTimeout(() => setActionMsg((cur) => (cur === msg ? null : cur)), 3500);
  };

  return (
    <div className="panel" style={{ position: "relative" }}>
      <div className="panel-head">
        <span className="ph-title">Talkgroups</span>
        <span className="ph-meta">
          {networkName ? `${networkName} \u00B7 ` : ""}
          {sorted.length} TG
          {liveCount > 0 ? ` \u00B7 ${liveCount} LIVE` : ""}
        </span>
      </div>
      <div className="panel-body">
        {sorted.length === 0 ? (
          <div className="empty">No talkgroups in this network</div>
        ) : (
          <div className="tg-grid">
            {sorted.map((g) => (
              <Tile
                key={g.ID}
                group={g}
                activity={activity.get(g.ID)}
                now={now}
                selected={g.ID === selectedId}
                isVisualTx={activePttGroupId === g.ID}
                muted={mutedGroupIds.has(g.ID)}
                pinned={pinnedGroupIds.has(g.ID)}
                onClick={() => onSelect(g.ID)}
                onPttStart={() => onPttStart(g.ID)}
                onPttStop={() => onPttStop(g.ID)}
                onUnsupportedAction={(label) => flash(`${label} NOT AVAILABLE`)}
              />
            ))}
          </div>
        )}
      </div>
      {actionMsg ? <div className="panel-toast">{actionMsg}</div> : null}
    </div>
  );
}

function Tile({
  group, activity, now, selected, isVisualTx, muted, pinned,
  onClick, onPttStart, onPttStop, onUnsupportedAction,
}: {
  group:      Group;
  activity:   TalkgroupActivity | undefined;
  now:        number;
  selected:   boolean;
  isVisualTx: boolean;
  muted:      boolean;
  pinned:     boolean;
  onClick:    () => void;
  onPttStart: () => void;
  onPttStop:  () => void;
  onUnsupportedAction: (label: string) => void;
}) {
  const isEmergency = group.Emergency === 1;
  const isAllCall   = group.AllCall === 1;
  const isBroadcast = group.Broadcast === 1;

  const state = activity?.state ?? "idle";
  const since = activity?.lastEventAt ? formatSince(now - activity.lastEventAt) : null;

  const cls = [
    "tg-tile",
    state === "rx"        ? "is-rx"        : "",
    state === "tx"        ? "is-tx"        : "",
    state === "hangtime"  ? "is-hangtime"  : "",
    isEmergency           ? "is-emergency" : "",
    selected              ? "is-selected"  : "",
    isVisualTx            ? "is-vtx"       : "",
    muted                 ? "is-muted"     : "",
    pinned                ? "is-pinned"    : "",
  ].filter(Boolean).join(" ");

  function start(e: React.PointerEvent | React.KeyboardEvent) {
    e.preventDefault();
    e.stopPropagation();
    onClick();
    onPttStart();
  }

  function stop(e: React.PointerEvent | React.KeyboardEvent) {
    e.preventDefault();
    e.stopPropagation();
    onPttStop();
  }

  return (
    <div
      className={cls}
      title={`Hold to talk on ${group.Name}`}
      role="button"
      tabIndex={0}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        start(e);
      }}
      onPointerUp={(e) => {
        try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
        stop(e);
      }}
      onPointerCancel={stop}
      onPointerLeave={(e) => { if ((e.buttons & 1) !== 0) stop(e); }}
      onKeyDown={(e) => {
        if (e.key !== " " && e.key !== "Enter") return;
        if (e.repeat) return;
        start(e);
      }}
      onKeyUp={(e) => {
        if (e.key !== " " && e.key !== "Enter") return;
        stop(e);
      }}
    >
      <div className="tg-head">
        <Priority p={group.Priority} />
        <span className="tg-name">{group.Name}</span>
        {isEmergency  ? <span className="tg-pill emg">EMG</span> : null}
        {isAllCall    ? <span className="tg-pill all">ALL</span> : null}
        {isBroadcast  ? <span className="tg-pill bc">BC</span>   : null}
        {pinned       ? <span className="tg-pill pin">PIN</span> : null}
        {muted        ? <span className="tg-pill muted">MUTED</span> : null}
      </div>

      <div className="tg-body">
        <div className="tg-last">
          {activity?.lastTalker ? (
            <>
              <div className="tg-last-row">
                <span className="t-mono tg-last-cs">{activity.lastTalker}</span>
              </div>
              <div className="tg-last-meta">
                <span className={"t-mono tg-since " + (state === "tx" ? "is-tx" : state === "rx" ? "is-rx" : state === "hangtime" ? "is-hang" : "")}>
                  {state === "tx"
                    ? "\u25CF TX"
                    : state === "rx"
                      ? "\u25CF RX"
                      : state === "hangtime"
                      ? "\u25CF HANG"
                      : `\u21BB ${since ?? "\u2014"}`}
                </span>
              </div>
            </>
          ) : (
            <div className="tg-last-meta tg-quiet">
              <span className="t-mono">IDLE</span>
            </div>
          )}
        </div>
      </div>

      <div className="tg-actions">
        <button
          type="button"
          className="tg-act ca"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onUnsupportedAction("CA"); }}
        >
          CA
        </button>
        <button
          type="button"
          className="tg-act pt"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onUnsupportedAction("PT"); }}
        >
          PT
        </button>
        <button
          type="button"
          className="tg-act emg"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onUnsupportedAction("EMG"); }}
        >
          EMG
        </button>
      </div>

      {isVisualTx ? (
        <div className="tg-vtx-overlay" aria-hidden="true">
          <span className="tg-vtx-label">VISUAL TX</span>
        </div>
      ) : null}
    </div>
  );
}

function Priority({ p }: { p: number | undefined }) {
  if (p == null || p === 0) return null;
  // -1 (low) .. 5 (highest). Map to a small pill.
  const cls = p >= 4 ? "tg-pri pri-1"
            : p >= 2 ? "tg-pri pri-2"
            : p > 0  ? "tg-pri pri-3"
                     : "tg-pri pri-low";
  const label = p > 0 ? `P${Math.min(5, p)}` : "P-";
  return <span className={cls}>{label}</span>;
}

function formatSince(ms: number): string {
  if (ms < 0) return "0s";
  const s = Math.floor(ms / 1000);
  if (s < 60)   return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60)   return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}
